import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { FileImage, Plus, Upload } from 'lucide-react';
import { LetterheadCard } from './letterhead-card';
import { LetterheadUploadDialog } from './letterhead-upload-dialog';
import {
  useGetOrgLetterheads,
  useCreateLetterhead,
  useUpdateLetterhead,
  useDeleteLetterhead,
} from '../hooks/use-org-letterheads';
import type { OrganizationLetterheadInsertInput } from '../types/org-letterheads.types';

interface LetterheadSectionProps {
  orgId: string;
}

export const LetterheadSection = ({ orgId }: LetterheadSectionProps) => {
  const { t } = useTranslation();
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  const { data, isLoading } = useGetOrgLetterheads(orgId);
  const createMutation = useCreateLetterhead(orgId);
  const updateMutation = useUpdateLetterhead(orgId);
  const deleteMutation = useDeleteLetterhead(orgId);

  const letterheads = data?.items || [];

  const handleUpload = async (input: OrganizationLetterheadInsertInput) => {
    await createMutation.mutateAsync(input);
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="aspect-8.5/11" />
        ))}
      </div>
    );
  }

  return (
    <>
      {letterheads.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="border-2 border-dashed border-border rounded-lg p-6 md:p-12 text-center">
              <div className="h-14 w-14 md:h-16 md:w-16 rounded-2xl bg-selise-blue/10 flex items-center justify-center mx-auto mb-4">
                <FileImage className="w-7 h-7 md:w-8 md:h-8 text-selise-blue" />
              </div>
              <h3 className="font-semibold text-base md:text-lg text-[hsl(var(--fg))] mb-2">
                {t('ORGSETTINGS_LETTERHEAD_EMPTYTITLE')}
              </h3>
              <p className="text-sm text-globe-grey mb-6 max-w-sm mx-auto">
                {t('ORGSETTINGS_LETTERHEAD_EMPTYDESCRIPTION')}
              </p>
              <Button size="sm" onClick={() => setShowUploadDialog(true)}>
                <Upload className="h-4 w-4 mr-2" />
                {t('ORGSETTINGS_LETTERHEAD_UPLOADFIRSTBUTTON')}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-globe-grey truncate">
              {t('ORGSETTINGS_LETTERHEAD_UPLOADEDCOUNT', { count: letterheads.length })}
            </p>
            <Button size="sm" className="shrink-0" onClick={() => setShowUploadDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t('ORGSETTINGS_LETTERHEAD_ADDBUTTON')}
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {letterheads.map((letterhead) => (
              <LetterheadCard
                key={letterhead.ItemId}
                letterhead={letterhead}
                orgId={orgId}
                onUpdate={updateMutation}
                onDelete={deleteMutation}
              />
            ))}
          </div>
        </div>
      )}

      <LetterheadUploadDialog
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        orgId={orgId}
        onUpload={handleUpload}
      />
    </>
  );
};
